import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';

import {API_URL} from '../constants/api-urls';
import {DiscountCode} from '../models/discount-code.model';
import {Response} from '../models/response.model';
import {DetailedError, findDetailedErrorMessage} from '../shared/helpers';

import {isEmptyArray} from '../shared/utils';
import {Address} from '../models/address.model';
import {AddressType} from '../enums/address-type';
import {AddressService} from './address.service';
import {OrderItem} from '../models/order-item.model';
import {Product} from '../models/product.model';

@Injectable()
export class DiscountCodeService {

  public discountCodes: DiscountCode[] = [];


  constructor(private addressService: AddressService, private httpClient: HttpClient, private toastr: ToastrService) {
  }


  public fetchDiscountCodes(): Observable<DiscountCode[]> {
    return this.httpClient.get<Response>(`${API_URL}/discount-codes`).pipe(
      map( (response: Response) => {
        if (isEmptyArray(response.data)) {
          return [];
        }

        this.discountCodes = response.data.map( i => new DiscountCode(i));
        return this.discountCodes;

    }),
      catchError(this.handleHttpError('DiscountCodeService:fetchDiscountCodes', []))
    );
  }


  private handleHttpError<T>(operation = 'operation', result?: T) {
    return (error: DetailedError): Observable<T> => {

      const errorMessage = findDetailedErrorMessage(error);
      if (errorMessage) {
        this.toastr.error(errorMessage);
        console.log(operation + ' failed', error);
      }
      return of(result as T);
    };
  }


  public get discountCodesForPersonalAddresses(): DiscountCode[] {
    return this.discountCodes.filter( i => i.personal_addresses_allowed);
  }


  public getDiscountCodeById(id: number | null | undefined): DiscountCode | null {
    if (!id) {
      return null;
    }
    return this.discountCodes.find( i => i.id === id) ?? null;
  }

  public get hasDiscountCodes(): boolean {
    return !isEmptyArray(this.discountCodes);
  }

    public getDiscountCodesForAddress(
        address: Address,
        orderItem: OrderItem | null = null
    ): DiscountCode[] {
        if (!address) {
            return [];
        }

        const isPersonal = address.address_type === AddressType.Personal;

        return this.discountCodes.filter(d => {
            const productEligible = this.isProductEligibleForDiscount(orderItem?.product, d);
            if (!productEligible) {
                return false;
            }

            // PERSONAL ADDRESS RULES
            if (isPersonal) {
                if (d.personal_addresses_allowed !== true) {
                    return false;
                }
                // For personal addresses, ONLY show if explicitly assigned via app_coupon_id
                return !!address.app_coupon_id && d.id === address.app_coupon_id;
            }

            // NON-PERSONAL ADDRESS RULES
            if (!d.assigned_addresses || d.assigned_addresses.length === 0) {
                return true;
            }

            return d.assigned_addresses.some(a =>
                Number(a.address_id) === Number(address.id) &&
                Number(a.address_type) === Number(address.address_type)
            );
        });
    }

  public isProductEligibleForDiscount(product: Product, discount: DiscountCode): boolean {
    // Early validation
    if (
        !product?.attributes?.length ||
        !discount?.discount_condition?.length ||
        !['CATEGORY', 'PROGRAM', 'BRAND'].includes(discount.discount_category)
    ) {
      return false;
    }

    const { discount_category, discount_condition } = discount;

    const conditionSet = new Set<string>(
        discount_condition
            .filter((c): c is { selection: string; sub_type: string | null } => !!c?.selection)
            .map((c) => `${c.selection}|${c.sub_type ?? 'null'}`)
    );

    // Early return for invalid attributes
    if (!product.attributes.some((attr) => attr?.label === discount_category)) {
      return false;
    }

    for (const attribute of product.attributes) {
      if (attribute?.label !== discount_category) {
        continue;
      }

      switch (discount_category) {
        case 'CATEGORY':
          if (!product.category_external_id) {
            break;
          }
          return (
              conditionSet.has(`${product.category_external_id}|${product.productType ?? 'null'}`) ||
              conditionSet.has(`${product.category_external_id}|null`)
          );

        case 'PROGRAM':
          if (product.program_code) {
            return conditionSet.has(`${product.program_code}|null`);
          }
          break;

        case 'BRAND':
          return attribute.options?.some((option) => option?.label && conditionSet.has(`${option.label}|null`)) ?? false;
      }
    }

    return false;
  }

}
